import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { useState } from 'react';

import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';

interface CreateOrgDialogProps {
	open: boolean;
	onClose: () => void;
}

const STATE_LABELS: Record<string, string> = {
	pending: 'Waiting…',
	cloning: 'Cloning…',
	cloned: '✓ Cloned',
	failed: '✗ Failed',
};

/**
 * Dialog for creating a new organization. When demo projects are configured,
 * the admin can pick a subset to clone into the new org and then watches live
 * cloning progress.
 */
export function CreateOrgDialog({ open, onClose }: CreateOrgDialogProps) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const addToast = useAddToast();
	const [orgDisplayName, setOrgDisplayName] = useState('');
	const [orgNameError, setOrgNameError] = useState<string | undefined>();
	const [submitError, setSubmitError] = useState<string | undefined>();
	const [selectedDemoIds, setSelectedDemoIds] = useState<Set<string>>(new Set());
	const [seedWorkflowId, setSeedWorkflowId] = useState<string | undefined>();

	const { data: demoData } = useQuery({
		...trpc.organization.listDemoProjects.queryOptions({}),
		enabled: open && seedWorkflowId === undefined,
	});
	const demoProjects = demoData?.projects ?? [];

	const { data: seedStatus, error: seedError } = useQuery({
		...trpc.organization.getDemoSeedStatus.queryOptions({ workflowId: seedWorkflowId ?? '' }),
		enabled: seedWorkflowId !== undefined,
		// Keep polling until a terminal status is reached; an errored/undefined poll
		// (e.g. a transient progress-query failure) must retry, not stop.
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return status === 'completed' || status === 'failed' || status === 'notFound' ? false : 1500;
		},
	});

	const { mutateAsync: createOrg, isPending } = useMutation(
		trpc.organization.create.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: trpc.organization.getMany.queryKey() });
			},
		}),
	);

	const isSeeding = seedWorkflowId !== undefined;
	const seedTerminal = seedStatus !== undefined && seedStatus.status !== 'running';
	// Let the user finish either when seeding reaches a terminal status or when
	// progress can't be read at all — never leave the dialog stuck.
	const seedComplete = seedTerminal || seedError !== null;

	const resetAndClose = () => {
		setOrgDisplayName('');
		setOrgNameError(undefined);
		setSubmitError(undefined);
		setSelectedDemoIds(new Set());
		setSeedWorkflowId(undefined);
		onClose();
	};

	const handleClose = () => {
		// Block dismissal (backdrop/Escape) while the org is being created or a
		// clone is still in flight, so the seeding summary is not skipped.
		if (isPending || (isSeeding && !seedComplete)) {
			return;
		}
		resetAndClose();
	};

	const toggleDemo = (projectId: string) => {
		setSelectedDemoIds((previous) => {
			const next = new Set(previous);
			if (next.has(projectId)) {
				next.delete(projectId);
			} else {
				next.add(projectId);
			}
			return next;
		});
	};

	const handleSubmit = async () => {
		setOrgNameError(undefined);
		setSubmitError(undefined);

		const displayName = orgDisplayName.trim();
		const organizationName = deriveOrgSlug(displayName);
		if (organizationName.length < 3) {
			setOrgNameError('Use a name with at least 3 letters or numbers.');
			return;
		}

		try {
			const demoProjectIds = Array.from(selectedDemoIds);
			const result = await createOrg({ organizationName, displayName, demoProjectIds });
			if (result.seedWorkflowId !== undefined) {
				setSeedWorkflowId(result.seedWorkflowId);
			} else {
				// The org was created; if demos were requested but no workflow started
				// (nothing published matched, or seeding couldn't start), say so rather
				// than closing silently.
				if (demoProjectIds.length > 0) {
					addToast(<Alert severity='warning'>Organization created, but demo seeding could not be started.</Alert>);
				}
				resetAndClose();
			}
		} catch (err) {
			if (err instanceof TRPCClientError) {
				if (err.message.toLowerCase().includes('already') || err.message.toLowerCase().includes('exist')) {
					setOrgNameError(err.message);
				} else {
					setSubmitError(err.message);
				}
			} else {
				setSubmitError('An unexpected error occurred.');
			}
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			{isSeeding ?
				<>
					<DialogTitle>Creating organization</DialogTitle>
					<DialogContent className='flex flex-col gap-4 pt-2'>
						<Typography variant='sm'>
							The organization was created. Cloning {seedStatus?.total ?? selectedDemoIds.size} demo project(s)…
						</Typography>
						<LinearProgress
							variant={seedStatus === undefined ? 'indeterminate' : 'determinate'}
							value={seedStatus && seedStatus.total > 0 ? (seedStatus.completed / seedStatus.total) * 100 : 0}
						/>
						<Box className='flex flex-col gap-1'>
							{(seedStatus?.perProject ?? []).map((project) => (
								<Box key={project.sourceProjectId} className='flex items-center justify-between gap-2'>
									<Typography variant='sm'>{project.name}</Typography>
									<Typography
										variant='xs'
										color={
											project.state === 'failed' ? 'error'
											: project.state === 'cloned' ?
												'success.main'
											:	'text.secondary'
										}
										title={project.reason}
									>
										{STATE_LABELS[project.state] ?? project.state}
									</Typography>
								</Box>
							))}
						</Box>
						{seedTerminal && (
							<Alert
								severity={
									seedStatus.status !== 'completed' ? 'error'
									: seedStatus.perProject.some((project) => project.state === 'failed') ?
										'warning'
									:	'success'
								}
							>
								{summarize(seedStatus)}
							</Alert>
						)}
						{!seedTerminal && seedError !== null && (
							<Alert severity='warning'>
								{"Couldn't check cloning progress. It may still be running — you can close this and check back later."}
							</Alert>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose} variant='contained' disabled={!seedComplete}>
							Done
						</Button>
					</DialogActions>
				</>
			:	<form
					onSubmit={(e) => {
						e.preventDefault();
						void handleSubmit();
					}}
				>
					<DialogTitle>Create organization</DialogTitle>
					<DialogContent className='flex flex-col gap-4 pt-2'>
						{submitError && (
							<Alert severity='error' onClose={() => setSubmitError(undefined)}>
								{submitError}
							</Alert>
						)}
						<TextField
							label='Organization name'
							value={orgDisplayName}
							onChange={(e) => setOrgDisplayName(e.target.value)}
							required
							fullWidth
							error={Boolean(orgNameError)}
							helperText={orgNameError ?? 'Spaces and punctuation are allowed. Must be at least 3 characters.'}
							disabled={isPending}
							autoFocus
						/>
						{demoProjects.length > 0 && (
							<Box className='flex flex-col gap-1'>
								<Typography variant='sm'>Seed with demo projects (optional)</Typography>
								{demoProjects.map((project) => (
									<FormControlLabel
										key={project.id}
										control={
											<Checkbox
												checked={selectedDemoIds.has(project.id)}
												onChange={() => toggleDemo(project.id)}
												disabled={isPending}
											/>
										}
										label={
											<Box className='flex flex-col'>
												<Typography variant='sm'>{project.name}</Typography>
												{project.description.length > 0 && (
													<Typography variant='xs' color='text.secondary'>
														{project.description}
													</Typography>
												)}
											</Box>
										}
									/>
								))}
							</Box>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} disabled={isPending}>
							Cancel
						</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={isPending || orgDisplayName.trim().length < 3}
							loading={isPending}
						>
							Create
						</Button>
					</DialogActions>
				</form>
			}
		</Dialog>
	);
}

/**
 * Derives org slug by removing spaces and punctuation, and lowercasing the string.
 * @param displayName - The human-facing organization name.
 * @returns The derived slug, which the caller validates for minimum length.
 */
function deriveOrgSlug(displayName: string): string {
	return displayName.toLowerCase().replace(/[^a-z0-9_]/g, '');
}

/**
 * Builds the human-readable summary shown when cloning finishes.
 */
function summarize(
	status: { status: string; total: number; perProject: Array<{ state: string; name: string }> } | undefined,
): string {
	if (status === undefined) {
		return '';
	}
	if (status.status !== 'completed') {
		return "Couldn't clone demo projects: the seeding run didn't complete.";
	}
	const cloned = status.perProject.filter((project) => project.state === 'cloned').length;
	const failedNames = status.perProject.filter((project) => project.state === 'failed').map((project) => project.name);
	if (failedNames.length === 0) {
		return `${cloned} of ${status.total} demo project(s) cloned.`;
	}
	return `${cloned} of ${status.total} cloned. Couldn't clone: ${failedNames.join(', ')}.`;
}
